import React, { useEffect, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useAtom } from 'jotai';
import { Navigate } from 'react-router-dom';

import {
  uiRoutes,
  useAccountInfo,
  useTracking,
} from '@consumer-frontend/nc-api';
import {
  isTruthy,
  MAKE_PAYMENT_RESPONSES_MAP,
  PaymentDetailsT,
  today,
} from '@consumer-frontend/nc-utils';
import { Container } from '@consumer-frontend/shared-ui';

import {
  accountIdAtom,
  paymentDetailsAtom,
} from '../../store';
import { NcAlert } from '../NcAlert';
import { NcGoHomeButton } from '../NcGoHomeButton';
import { NcReviewPaymentFields } from '../NcReviewPaymentDetail';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/payment/confirmation.scss';

export const NcPaymentConfirmation: React.FC = () => {
  const [accountId] = useAtom(accountIdAtom);
  const { accountInfo } = useAccountInfo(accountId);
  const {
    email,
    loanNumber,
    loanType,
  } = accountInfo || {};

  const { trackEvent } = useTracking(accountId);
  const hasFiredTrackingEvent = React.useRef(false);

  const [paymentDetails, setPaymentDetails] = useAtom(paymentDetailsAtom);
  const [localPaymentDetails, setLocalPaymentDetails] = useState<PaymentDetailsT | undefined>();

  const { successType } = paymentDetails || localPaymentDetails || {};

  // Clear payment details from global state after loading confirmation page
  useEffect(
    () => {
      if (!paymentDetails) return;

      window.scrollTo(0, 0);
      setLocalPaymentDetails((prev) => ({
        ...prev,
        ...paymentDetails,
      }));
      setPaymentDetails(() => undefined);
    },
    [paymentDetails, setPaymentDetails],
  );

  // Track page views in Datadog and internal event tracking
  useEffect(
    () => {
      if (!paymentDetails || hasFiredTrackingEvent.current) { return; }

      datadogRum.addAction('NcMakeAPaymentConfirmationView');

      hasFiredTrackingEvent.current = true;
      // This setTimeout is necessary to ensure the page transition happening simultaneously completes
      // before the trackEvent fires in order to associate the event with the correct page_view_id.
      setTimeout(
        async () => {
          try {
            await trackEvent({
              event_data: {
                full_payoff_amount: paymentDetails.fullPayoffAmount,
                loan_number: loanNumber,
                num_days_until_target_date: paymentDetails.paymentDate.compare(today()),
                payment_amount: paymentDetails.paymentAmount,
                payment_messaging: paymentDetails.paymentMessaging,
                payment_method: paymentDetails.paymentMethod,
                payment_target_date: paymentDetails.paymentDate.toString(),
                product: loanType,
                success_type: successType,
              },
              event_description: 'paymentConfirmationLoaded',
            });
          } catch {
            /* Swallow tracking errors */
          }
        },
        1000,
      );
    },
    [
      loanNumber,
      loanType,
      paymentDetails,
      successType,
      trackEvent,
    ],
  );

  // Redirect to make a payment page if no payment details are available
  if (!paymentDetails && !localPaymentDetails) {
    return <Navigate to={uiRoutes.dashboard.makeAPayment} replace />;
  }

  return (
    <Container className="payment-confirmation-page" hasBoxShadow>
      {email && isTruthy(successType) && (
        <>
          <NcAlert
            dismissible={false}
            header={`Payment ${MAKE_PAYMENT_RESPONSES_MAP[successType]}. Thank you!`}
            variant="success"
          >
            <p>A confirmation email will be sent to <b className="non-overflowing">{email}</b>.</p>
          </NcAlert>

          <hr />
        </>
      )}

      <h1 className="h2">Payment Details</h1>

      <>
        {localPaymentDetails && <NcReviewPaymentFields {...localPaymentDetails} />}

        <NcGoHomeButton />
      </>
    </Container>
  );
};
